import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, File, AlertCircle, CheckCircle, Cloud, FolderOpen } from 'lucide-react';
import PopupTransition from '@/components/ui/PopupTransition';
import { useAppDispatch } from '@/data/store/utils/redux';
import { createFileAsync } from '@/data/store/actions';
import { TFilePayload } from '@/data/types';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onUploadSuccess?: () => void;
  initialFiles?: File[];
}

interface UploadingFile {
  file: File;
  id: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  currentPath,
  onUploadSuccess,
  initialFiles = [],
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Set initial files when modal opens
  useEffect(() => {
    if (isOpen && initialFiles.length > 0) {
      setSelectedFiles(initialFiles);
    }
  }, [isOpen, initialFiles]);

  const handleClose = useCallback(() => {
    setSelectedFiles([]);
    setUploadingFiles([]);
    onClose();
  }, [onClose]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    const uploadFiles: UploadingFile[] = selectedFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadingFiles(uploadFiles);
    setSelectedFiles([]);

    // Upload files one by one
    for (const uploadFile of uploadFiles) {
      try {
        const payload: TFilePayload = {
          name: uploadFile.file.name,
          path: currentPath,
          file: uploadFile.file,
          content_type: uploadFile.file.type,
        };

        await dispatch(
          createFileAsync({
            payload,
            onProgress: (progress) => {
              setUploadingFiles((prev) =>
                prev.map((uf) => (uf.id === uploadFile.id ? { ...uf, progress: Math.round(progress) } : uf)),
              );
            },
          }),
        );

        // Mark as success
        setUploadingFiles((prev) =>
          prev.map((uf) => (uf.id === uploadFile.id ? { ...uf, status: 'success' as const, progress: 100 } : uf)),
        );
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.id === uploadFile.id
              ? {
                  ...uf,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : uf,
          ),
        );
      }
    }

    // Call success callback to refresh the file list
    if (onUploadSuccess) {
      onUploadSuccess();
    }

    // Auto-close after a short delay if all uploads succeeded
    setTimeout(() => {
      const allSuccessful = uploadFiles.every((uf) => {
        const current = uploadingFiles.find((uuf) => uuf.id === uf.id);
        return current?.status === 'success';
      });

      if (allSuccessful) {
        handleClose();
      }
    }, 2000);
  }, [selectedFiles, currentPath, dispatch, onUploadSuccess, handleClose, uploadingFiles]);

  const isUploading = uploadingFiles.some((uf) => uf.status === 'uploading');
  const allCompleted = uploadingFiles.length > 0 && uploadingFiles.every((uf) => uf.status !== 'uploading');

  return (
    <PopupTransition open={isOpen} onClose={handleClose}>
      <div className="relative w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl bg-white/20 p-3 backdrop-blur-sm">
                <Cloud className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Μεταφόρτωση αρχείων</h3>
                <p className="mt-1 text-sm text-blue-100">Μεταφορτώστε τα αρχεία σας γρήγορα και με ασφάλεια</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="rounded-xl p-2 text-white/80 transition-all duration-200 hover:bg-white/20 hover:text-white disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Subtitle */}
        <div className="border-b border-gray-100 bg-blue-50/50 px-8 py-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Προορισμός: {currentPath === '/' ? 'Root Directory' : currentPath}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-8">
          {/* Drop Zone */}
          <div
            className={`relative rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-300 ${
              dragActive
                ? 'scale-105 border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/30'
            } `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleInputChange}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              disabled={isUploading}
            />

            <div className="space-y-6">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-blue-100 p-4">
                <Upload className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <p className="mb-2 text-xl font-semibold text-gray-900">Σύρετε αρχεία εδώ ή κάντε κλικ για επιλογή</p>
                <p className="mb-1 text-base text-gray-600">Υποστηρίζονται όλοι οι τύποι αρχείων</p>
                <p className="text-sm text-gray-500">Μέγιστο μέγεθος: 100MB ανά αρχείο</p>
              </div>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-8">
              <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <File className="h-5 w-5 text-blue-600" />
                Επιλεγμένα αρχεία ({selectedFiles.length})
              </h4>
              <div className="max-h-48 space-y-3 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-xl border-2 border-blue-100 bg-blue-50/30 p-4 transition-all duration-200 hover:bg-blue-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="rounded-lg bg-blue-100 p-2">
                        <File className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-600">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="rounded-xl p-2 text-gray-400 transition-all duration-200 hover:bg-red-100 hover:text-red-600"
                      disabled={isUploading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploading Files */}
          {uploadingFiles.length > 0 && (
            <div className="mt-8">
              <h4 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Cloud className="h-5 w-5 text-blue-600" />
                Κατάσταση μεταφόρτωσης
              </h4>
              <div className="max-h-64 space-y-4 overflow-y-auto">
                {uploadingFiles.map((uploadFile) => (
                  <div key={uploadFile.id} className="rounded-xl border-2 border-gray-100 bg-white p-5 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`rounded-lg p-2 ${
                            uploadFile.status === 'success'
                              ? 'bg-green-100'
                              : uploadFile.status === 'error'
                                ? 'bg-red-100'
                                : 'bg-blue-100'
                          }`}
                        >
                          {uploadFile.status === 'success' ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : uploadFile.status === 'error' ? (
                            <AlertCircle className="h-5 w-5 text-red-600" />
                          ) : (
                            <File className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{uploadFile.file.name}</p>
                          <p className="text-xs text-gray-600">{formatFileSize(uploadFile.file.size)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {uploadFile.status === 'success' && (
                          <span className="rounded-lg bg-green-50 px-2 py-1 text-xs font-semibold text-green-600">
                            Ολοκληρώθηκε
                          </span>
                        )}
                        {uploadFile.status === 'error' && (
                          <span className="rounded-lg bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">
                            Σφάλμα
                          </span>
                        )}
                        {uploadFile.status === 'uploading' && (
                          <span className="rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600">
                            {uploadFile.progress}%
                          </span>
                        )}
                      </div>
                    </div>

                    {uploadFile.status === 'uploading' && (
                      <div className="h-3 w-full rounded-full bg-gray-200">
                        <div
                          className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out"
                          style={{ width: `${uploadFile.progress}%` }}
                        />
                      </div>
                    )}

                    {uploadFile.error && (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                        <p className="text-xs text-red-700">{uploadFile.error}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-8 py-6">
          {selectedFiles.length > 0 || uploadingFiles.length > 0 ? (
            <div className="text-sm text-gray-600">
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  Μεταφόρτωση σε εξέλιξη...
                </span>
              ) : allCompleted ? (
                <span className="font-medium text-green-600">Η μεταφόρτωση ολοκληρώθηκε!</span>
              ) : (
                `${selectedFiles.length} αρχεί${selectedFiles.length === 1 ? 'ο' : 'α'} έτοιμ${selectedFiles.length === 1 ? 'ο' : 'α'} για μεταφόρτωση`
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Επιλέξτε αρχεία για μεταφόρτωση</div>
          )}
          <div className="flex gap-4">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="rounded-xl border-2 border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 focus:outline-none disabled:opacity-50"
            >
              {allCompleted ? 'Κλείσιμο' : 'Ακύρωση'}
            </button>
            {selectedFiles.length > 0 && (
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg focus:ring-4 focus:ring-blue-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Μεταφόρτωση...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Μεταφόρτωση {selectedFiles.length} αρχεί{selectedFiles.length === 1 ? 'ου' : 'ων'}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </PopupTransition>
  );
};

export default FileUploadModal;
